import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { Phase, Price } from 'Cube/domain/cube.domain.types'
import {
  CopyTargetPhase,
  getPriceCopyTargets,
  getPriceCopyUpdate
} from 'Cube/utils/copyToPhase'
import { useFlashEntities } from 'Cube/utils/useFlashEntities'
import { useMemo } from 'react'

export type CopyToPhase = {
  targets: CopyTargetPhase[]
  copyTo: (targetPhaseId: Phase['id']) => void
}

/**
 * Offers the phases a price in this phase can be copied to, and applies the
 * copy. Every other phase is a target, disabled where it cannot take the price.
 */
export const useCopyPriceToPhase = (props: { phaseId: Phase['id'] }) => {
  const cubeContext = useCubeContext()
  const flashEntities = useFlashEntities()

  const { resolvedPhases, orderedPhases, availableFeatures } =
    cubeContext.queries
  const sourcePhase = resolvedPhases[props.phaseId]

  return useMemo(
    () =>
      (price: Price): CopyToPhase => ({
        targets: getPriceCopyTargets({
          price,
          sourcePhase,
          orderedPhases,
          availableFeatures: availableFeatures.phases
        }),
        /**
         * The phase can be deleted between the menu rendering and a click on
         * it, so the copy resolves its destination at click time.
         */
        copyTo: targetPhaseId => {
          const targetPhase = resolvedPhases[targetPhaseId]
          const targetFeatures = availableFeatures.phases[targetPhaseId]

          if (!targetPhase || !targetFeatures) {
            return
          }

          const result = getPriceCopyUpdate({
            price,
            sourcePhase,
            targetPhase,
            availableFeatures: targetFeatures
          })

          cubeContext.mutators.updateData(result.update)
          flashEntities(result.copiedEntityIds)
        }
      }),
    [
      orderedPhases,
      availableFeatures.phases,
      resolvedPhases,
      sourcePhase,
      cubeContext.mutators,
      flashEntities
    ]
  )
}
